/**
 * useNativeCallKit — iOS-only hook that wires PushKit + CallKit to the app.
 *
 * Flow:
 *   1. After login, ask the native plugin to register for PushKit VoIP pushes.
 *   2. Send the VoIP token to the backend prefixed with "voip:" (the backend
 *      routes these through the APNs VoIP sender and iOS renders CallKit UI
 *      when a push arrives).
 *   3. Listen to CallKit Accept events and navigate into /call/:callId.
 *   4. On logout: delete the token from the backend and tear down listeners.
 *
 * Android and Web are a no-op — Android uses FCM high-priority data +
 * ConnectionService (see the `calls/` package in the Android app).
 *
 * Apple contract enforced in the native layer (CallKitManager.swift):
 *   - every VoIP push is followed by CXProvider.reportNewIncomingCall
 *   - topic = "<bundleId>.voip"
 *   - pushType = .voIP
 */

import { useCallback, useEffect, useRef } from "react";
import { Capacitor, registerPlugin, PluginListenerHandle } from "@capacitor/core";
import useAuthUser from "./useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import providers from "lib/providers";

/** Prefix that marks this token as an APNs VoIP token on the backend side. */
const VOIP_TOKEN_PREFIX = "voip:";
const LOG = "[CallKit]";

type RegistrationEvent = { token?: string } | string;
type CallAnsweredEvent = { callId?: string; connectionId?: string };
type CallEndedEvent = { callId?: string; reason?: string };

interface CallKitVoipPlugin {
  register(): Promise<void>;
  unregister?: () => Promise<void>;
  /** One-shot query for a call the user accepted before the WebView was ready. */
  getPendingAnswer?: () => Promise<{ callId: string }>;
  /** Synchronous pull of the cached VoIP token — defeats the login-vs-PushKit race. */
  getToken?: () => Promise<{ token: string }>;
  addListener(event: "registration", listener: (p: RegistrationEvent) => void): Promise<PluginListenerHandle>;
  addListener(event: "callAnswered", listener: (p: CallAnsweredEvent) => void): Promise<PluginListenerHandle>;
  addListener(event: "callEnded", listener: (p: CallEndedEvent) => void): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

const CallKitVoip = registerPlugin<CallKitVoipPlugin>("CallKitVoip");
const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

/** Lightweight breadcrumb — visible in Safari Web Inspector when attached to the
 *  iOS WebView. Kept as a function (rather than inline console.log) so it's
 *  trivial to silence, reroute, or re-enable persistent logging in the future. */
function xlog(msg: string): void {
  // eslint-disable-next-line no-console
  console.log(LOG, msg);
}

export default function useNativeCallKit() {
  const { user } = useAuthUser();

  // Fires on every render. If we see "mount hasUser=false" but never
  // "mount hasUser=true" it means login didn't propagate to useAuthUser.
  // If we never see any "mount" line it means the WebPushSubscriber that
  // hosts this hook is never rendered (App gate problem).
  xlog(`hook render hasUser=${!!user}${user ? ` id=${user.id}` : ""}`);

  // refs so cleanup() can read the latest user/token without being a
  // dependency of its own useCallback (which would re-subscribe the
  // emitter listener on every user change).
  const userRef = useRef(user);
  userRef.current = user;

  const registeredRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const handlesRef = useRef<PluginListenerHandle[]>([]);
  /** callIds whose user-decline we already relayed to the backend — CallKit
   *  can emit callEnded more than once per call (provider reset, re-report). */
  const endedRelayedRef = useRef<Set<string>>(new Set());
  /** callIds the user answered in the CallKit UI on THIS device: a later
   *  userEnded for one of these is a genuine hang-up (end() unconditionally);
   *  for anything else it's a decline-while-ringing and must be gated on the
   *  call still being NEW server-side. */
  const answeredHereRef = useRef<Set<string>>(new Set());

  const register = useCallback(async () => {
    xlog(`register() entry isIOS=${isIOS} hasUser=${!!user} alreadyRegistered=${registeredRef.current}`);
    if (!isIOS || !user) return;
    // Atomic guard FIRST, before any await — otherwise a second invocation
    // triggered by React StrictMode or a fast re-render during the awaits
    // below would see `false` and double-register (duplicate listeners,
    // duplicate token POST). Revert on error so a genuine failure doesn't
    // wedge the hook permanently.
    if (registeredRef.current) return;
    registeredRef.current = true;
    xlog(`register() proceeding for userId=${user.id}`);

    const sendToken = async (rawToken: string) => {
      const prefixed = VOIP_TOKEN_PREFIX + rawToken;
      if (tokenRef.current === prefixed) return; // dedupe between event + getToken paths
      tokenRef.current = prefixed;
      xlog(`sendToken: POSTing voip:${rawToken.substring(0, 16)}... for userId=${user.id}`);
      try {
        await providers.PushNotificationsProvider.pushNotificationsAddToken({
          data: { token: prefixed, userId: user.id },
        });
        xlog("sendToken: backend accepted VoIP token");
      } catch (err) {
        // Don't clear tokenRef — next register() will retry via the
        // getToken() sync-pull path if the token is still cached.
        tokenRef.current = null;
        xlog(`sendToken: FAILED ${String((err as Error)?.message ?? err)}`);
        console.error(LOG, "Failed to send VoIP token:", err);
      }
    };

    try {
      // Event path — catches tokens that arrive AFTER this hook mounted.
      handlesRef.current.push(
        await CallKitVoip.addListener("registration", (ev) => {
          const rawToken = typeof ev === "string" ? ev : ev?.token;
          if (rawToken) void sendToken(rawToken);
        })
      );

      // User tapped Accept in the native CallKit UI → delegate to the
      // in-page IncomingCallModal so the SAME `handleAccept` flow runs
      // as for in-page button taps.
      //
      // Why we no longer submit the accept mutation / navigate here:
      // 1-on-1 direct calls need `callConnection` (the WebRTC signalling
      // handle from the GraphQL `subscribeIncomingCall` payload) passed
      // as route state into `/call/:id`. Without it, both participants
      // land on the call page with no shared signalling reference and
      // stick on "Connecting..." forever. That handle only exists inside
      // `IncomingCallModal`'s React state, so only IT can navigate
      // correctly. We just relay the Accept intent via an emitter event.
      //
      // Group conferences happen to work either way because
      // `/conference/:subjectId` joins by room id and does not need
      // `callConnection` — which matches what the user is seeing
      // ("групповые работают, 1-on-1 Connecting… у обоих").
      handlesRef.current.push(
        await CallKitVoip.addListener("callAnswered", (ev) => {
          const callId = String(ev?.callId ?? ev?.connectionId ?? "");
          if (!callId) return;
          xlog(`callAnswered callId=${callId} — delegating to IncomingCallModal`);
          answeredHereRef.current.add(callId);
          emitter.emit(EVENTS.CALL_ANSWERED_EXTERNALLY, { callId });
        })
      );

      // Call ended remotely (caller hung up, callee picked up on another
      // device, CallKit auto-timeout). Relay through the emitter so
      // IncomingCallModal — which owns the ringtone, the in-page UI and
      // the desktop overlay — can dismiss in lockstep with the native
      // CallKit UI. Without this relay, the React modal kept ringing
      // after the native screen had already cleared on multi-device
      // sign-in.
      handlesRef.current.push(
        await CallKitVoip.addListener("callEnded", (ev) => {
          console.log(LOG, "Ended", ev);
          const callId = String(ev?.callId ?? "");
          if (!callId) return;
          emitter.emit(EVENTS.CALL_CANCELLED_EXTERNALLY, { callId });
          // Relay the decline to the backend so the caller stops ringing
          // immediately. ONLY for reason="userEnded" — a genuine user action
          // in the CallKit UI on THIS device (older native builds send no
          // reason → behaviour unchanged). "cancelPush" must never relay:
          // it also fires for answered-elsewhere, and end()ing an ACCEPTED
          // call would kill the live call on the other device.
          if (ev?.reason === "userEnded" && !endedRelayedRef.current.has(callId)) {
            endedRelayedRef.current.add(callId);
            const relayEnd = async () => {
              try {
                // Hang-up of a call answered ON THIS device — a plain end().
                if (answeredHereRef.current.has(callId)) {
                  await providers.CallsProvider.end({ callId });
                  return;
                }
                // Decline while ringing. Guard against the answered-elsewhere
                // window (web accept committed, APNs cancel push still in
                // flight): only end() if the server still lists this call as
                // ringing for us — findActive returns NEW-status calls only,
                // so an already-ACCEPTED live call can never be killed here.
                const active = await providers.CallsProvider.findActive();
                if (active.some((c) => String(c.call.id) === callId)) {
                  await providers.CallsProvider.end({ callId });
                } else {
                  xlog(`callEnded relay skipped — call ${callId} no longer ringing (answered elsewhere?)`);
                }
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn(LOG, "decline relay failed:", e);
              }
            };
            relayEnd();
          }
        })
      );

      await CallKitVoip.register();
      xlog("PushKit native register() resolved");

      // Sync-pull — defeats the login-vs-PushKit race where PushKit delivered
      // the token before this hook's listener existed, so the event was
      // dropped by Capacitor. If the native layer has a cached token, we
      // send it here without depending on event timing.
      try {
        const pull = await CallKitVoip.getToken?.();
        xlog(`getToken returned token=${pull?.token ? pull.token.substring(0, 16) + "..." : "EMPTY"}`);
        if (pull?.token) {
          await sendToken(pull.token);
        }
      } catch (err) {
        // Older native builds don't expose getToken — fall back silently
        // to the event-based path.
        xlog(`getToken threw: ${String((err as Error)?.message ?? err)}`);
      }

      // Cold-launch-from-Accept recovery: if the user tapped Accept while
      // the WebView was still mounting, the native side buffered the
      // callId in `pendingAnsweredCallId`. The matching `callAnswered`
      // event in `CallKitManager.pendingEvents` is drained when we
      // attach `eventHandler` above, but the drain calls
      // `notifyListeners` BEFORE this hook has finished registering
      // its JS listener — so the event is silently dropped by
      // Capacitor's bridge (no JS subscriber yet).
      //
      // Replay the missed event ourselves by emitting
      // `CALL_ANSWERED_EXTERNALLY`. `IncomingCallModal` is the single
      // place that holds the subscription's `incomingCall.connection`
      // (the WebRTC signalling handle 1-on-1 needs in route state),
      // and it has a findActive() fallback if the subscription is
      // still in-flight when this fires.
      try {
        const pending = await CallKitVoip.getPendingAnswer?.();
        if (pending?.callId) {
          xlog(`draining pending answer callId=${pending.callId} — delegating to IncomingCallModal`);
          // Mirror the live callAnswered listener: without this, a later
          // CallKit hang-up of a cold-start-answered call would be
          // mis-classified as decline-while-ringing and silently skipped.
          answeredHereRef.current.add(String(pending.callId));
          emitter.emit(EVENTS.CALL_ANSWERED_EXTERNALLY, { callId: pending.callId });
        }
      } catch (err) {
        console.debug(LOG, "getPendingAnswer unavailable:", err);
      }
    } catch (err) {
      // Roll back the guard so a retry is possible (e.g. user re-login).
      registeredRef.current = false;
      xlog(`register() FAILED ${String((err as Error)?.message ?? err)}`);
      console.error(LOG, "Registration failed:", err);
    }
  }, [user]);

  const cleanup = useCallback(async () => {
    if (!isIOS) return;
    const currentUser = userRef.current;
    try {
      if (tokenRef.current && currentUser) {
        await providers.PushNotificationsProvider.pushNotificationsRemoveToken({
          data: { token: tokenRef.current, userId: currentUser.id },
        });
      }
      await Promise.allSettled(handlesRef.current.map((h) => h.remove()));
      handlesRef.current = [];
      await CallKitVoip.removeAllListeners();
      // unregister is intentionally a no-op on the native side (keeping
      // the PKPushRegistry delegate alive for the process lifetime is
      // required by iOS — detaching it mid-run SIGABRTs the process on
      // the next VoIP push). We still invoke it so a future native
      // implementation change is picked up automatically.
      await CallKitVoip.unregister?.();
      registeredRef.current = false;
      tokenRef.current = null;
      console.log(LOG, "Cleaned up");
    } catch (err) {
      console.error(LOG, "Cleanup failed:", err);
    }
  }, []);

  // Register after login.
  useEffect(() => {
    if (user && isIOS) void register();
  }, [user, register]);

  // Cleanup on logout. Listens to SET_LOGIN_DATA (rather than watching
  // `user` going falsy in a useEffect) because the auth store clears
  // the token from backend asynchronously — we need to fire our
  // pushNotificationsRemoveToken BEFORE the user context is gone from
  // the hook's closure. The emitter event is synchronous with logout.
  useEffect(() => {
    const onLoginDataChange = (data: { user?: unknown } | null | undefined) => {
      if (!data?.user) void cleanup();
    };
    emitter.on(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    return () => {
      emitter.off(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    };
  }, [cleanup]);
}
